import { useEffect, useRef, useState } from "react";
import {
	getPlanDetails,
	getPlans,
	newSubscription,
} from "../../../../services/manager/signature.service";
import { Controller, set, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import moment from "moment";
import { formatDateDB } from "../../../../../helpers";
import { toast } from "react-toastify";
import { CreateSubscriptionWrapper } from "./styles";
import { Button, CloseButton, Col, Form, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Dropdown } from "primereact/dropdown";
import Error from "../../../../components/Error";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { FaPlusCircle } from "react-icons/fa";
import { getUsers } from "../../../../services/manager/user.service";
import { ContentHeader } from "../../styled";
import { useDebounce } from "../../../../../hooks/useDebounce";
import Avatar from "react-avatar";
import handleLongText from "../../../../utils/handleLongText/handleLongText";
import { colors } from "../../../../../theme/theme";
import { Checkbox } from "primereact/checkbox";

interface ICreateSubscription {}

const validationSchema = Yup.object({
	startDate: Yup.date().required("Este campo é obrigatório"),
	endDate: Yup.date()
		.when(
			"startDate",
			(startDate, schema) =>
				startDate &&
				schema.min(
					startDate,
					"A data final precisa ser maior do que a data inicial"
				)
		)
		.required("Este campo é obrigatório"),
	contractValue: Yup.string().required("Este campo é obrigatório"),
	attachedDocs: Yup.array(),
	plan: Yup.string().required("Este campo é obrigatório"),
	user: Yup.object().required("Este campo é obrigatório"),
}).required();

export const CreateSubscription: React.FC<ICreateSubscription> = (props) => {
	const { t: translate } = useTranslation();

	const {
		handleSubmit,
		control,
		reset,
		setValue,
		setError,
		formState: { errors: errors },
	} = useForm({
		resolver: yupResolver(validationSchema),
	});

	const inputFile = useRef<HTMLInputElement | null>(null);

	const [disableFields, setDisableFields] = useState<boolean>(true);
	const [currencyValue, setCurrencyValue] = useState<any>(null);
	const [attachedDocs, setAttachedDocs] = useState<any[]>([]);
	const [planSelected, setPlanSelected] = useState<string>("");
	const [planOptions, setPlanOptions] = useState<any[]>([]);
	const [minimumContractTime, setMinimumContractTime] = useState<number>(0);
	const [autoRenewal, setAutoRenewal] = useState(true);

	const [loading, setLoading] = useState(false);
	const [user, setUser] = useState<any>();
	const [userQuery, setUserQuery] = useState("");
	const [userOptions, setUserOptions] = useState([]);

	const onSubmit = (formData: any) => {
		const monthDiff = moment(formData.endDate)
			.add(1, "seconds")
			.diff(formData.startDate, "months");

		if (monthDiff < minimumContractTime) {
			setError("startDate", {
				type: "custom",
				message: `O período selecionado é inválido. O período mínimo é de ${minimumContractTime} meses`,
			});
			return;
		}

		if (user) {
			setLoading(true);

			let obj = {
				start: formatDateDB(formData.startDate),
				end: formatDateDB(formData.endDate),
				value: formData.contractValue,
				subscribe: user.id,
				files: attachedDocs,
				plan: planSelected,
				auto_renew: autoRenewal,
				is_active: true,
			};

			newSubscription(obj)
				.then((response) => {
					toast.success("Assinatura criada com sucesso!");
					reset();
					setAttachedDocs([]);
					setCurrencyValue(null);
					setLoading(false);
					setDisableFields(true);
					setPlanSelected("");
					setMinimumContractTime(0);
					setAutoRenewal(true);
					setUser(null);
				})
				.finally(() => {
					setLoading(false);
				});
		}
	};

	const handleAddDocs = (docs: any) => {
		setAttachedDocs([...attachedDocs, ...docs]);
		setValue("attachedDocs", [...attachedDocs, ...docs]);
	};

	const handleRemoveDoc = (indexDoc: any) => {
		let arr = attachedDocs.slice(0);
		arr.splice(indexDoc, 1);
		setAttachedDocs(arr);
		setValue("attachedDocs", arr);
	};

	const handleSetPlanDetails = (planId: number) => {
		getPlanDetails(planId).then((plan: any) => {
			let date = new Date();
			date.setMonth(new Date().getMonth() + 12);

			setCurrencyValue(plan.parcel);
			setMinimumContractTime(plan.minimum_contract);
			setValue("contractValue", plan.parcel);
			setValue("startDate", new Date());
			setValue("endDate", date);
		});
	};

	/** Atualiza a lista de usuários com base em userQuery
	 *
	 * Chamada com debounce para evitar muitas requisições
	 * Chamada no onFilter do dropdown
	 */
	const updateUserList = useDebounce(() => {
		getUsers(`q=${userQuery}`).then((users: any) => {
			setUserOptions(users.results);
		});
	}, 800);

	/** Template para o dropdown de usuários */
	const userTemplate = (option: any) => {
		if (option) {
			let name = option?.name ?? "Não informado";
			return (
				<div className="d-flex align-items-center gap-3">
					<Avatar
						size="20"
						style={{ cursor: "pointer" }}
						round={true}
						name={option.name !== "" ? option.name : "U S"}
						src={option.picture ?? ""}
						color={
							!option.picture ? colors.mainColors.blueberry : ""
						}
					/>

					<span className="subscriber-name">
						{handleLongText(name, 70)}
					</span>
				</div>
			);
		} else {
			return (
				<div style={{ paddingTop: 2 }}>
					<span>Selecione um usuário</span>
				</div>
			);
		}
	};

	/** Init - carregar a lista de planos e usuários */
	useEffect(() => {
		getPlans("status=True").then((plans: any) => {
			let arr: any = [];

			plans.map((plan: any) => {
				let obj = {
					value: plan.id,
					label: plan.name,
				};
				arr.push(obj);
			});

			setPlanOptions(arr);
		});

		getUsers().then((users: any) => {
			setUserOptions(users.results);
		});
	}, []);

	return (
		<CreateSubscriptionWrapper>
			<ContentHeader>
				<Row>
					<div className="title">
						{translate("manager.subscription.new")}
					</div>
				</Row>
			</ContentHeader>
			{/*  */}

			{loading ? (
				<div className="loading-container mt-5">
					<Spinner
						animation="border"
						variant="primary"
					/>
				</div>
			) : (
				<>
					<Col
						xs={12}
						md={6}
						className="mt-3 mb-3 text-darker-grey"
					>
						{/* Select usuário */}
						<Form.Group>
							<Form.Label className="text-darker-grey">
								{translate("manager.subscription.name")}:
							</Form.Label>

							<Dropdown
								className="select-form-default user-dropdown"
								value={user}
								options={userOptions}
								itemTemplate={userTemplate}
								optionLabel="name"
								valueTemplate={userTemplate}
								onChange={(e) => {
									setUser(e.value);
									setValue("user", e.value);
								}}
								placeholder="Selecione um usuário"
								filter
								onFilter={(e) => {
									setUserQuery(e.filter);
									updateUserList();
								}}
								showFilterClear
								resetFilterOnHide
								onHide={() => setUserQuery("")}
							/>

							{errors.user ? (
								<div className="w-100">
									{" "}
									<Error>{errors.user?.message}</Error>{" "}
								</div>
							) : (
								""
							)}
						</Form.Group>
					</Col>

					<Col
						xs={12}
						md={6}
						className="mb-3 text-darker-grey"
					>
						<Form.Group className="mb-2">
							<Form.Label className="text-darker-grey">
								{translate("manager.subscription.plan")}:
							</Form.Label>
							<>
								<Dropdown
									className="select-form-default"
									value={planSelected}
									options={planOptions}
									onChange={(e) => {
										setValue("plan", e.value);
										setPlanSelected(e.value);
										setDisableFields(false);
										handleSetPlanDetails(e.value);
									}}
									placeholder="Selecione um plano"
								/>
							</>
							{errors.plan ? (
								<div className="w-100">
									{" "}
									<Error>{errors.plan?.message}</Error>{" "}
								</div>
							) : (
								""
							)}
						</Form.Group>
					</Col>

					<Col
						xs={12}
						md={6}
						className="mb-3 text-darker-grey"
					>
						<Form.Label>
							{translate("manager.subscription.contractValue")}:
						</Form.Label>
						<br />
						<InputNumber
							disabled={disableFields}
							value={currencyValue}
							onValueChange={(e: any) => {
								setCurrencyValue(e.value);
								setValue("contractValue", e.value);
							}}
							mode="currency"
							currency="BRL"
							locale="pt-BR"
							placeholder="R$ 1.000,00"
							style={{ width: "100%", height: "40px" }}
						/>
						{errors.contractValue ? (
							<div className="w-100">
								{" "}
								<Error>
									{errors.contractValue?.message}
								</Error>{" "}
							</div>
						) : (
							""
						)}
					</Col>

					<Col
						xs={12}
						md={6}
						className="mb-3 text-darker-grey"
					>
						<Form.Label>
							{translate("manager.subscription.start")}:
						</Form.Label>
						<br />
						<Controller
							control={control}
							name="startDate"
							render={({ field }) => (
								<Calendar
									disabled={disableFields}
									value={field.value}
									onChange={field.onChange}
									onMonthChange={() => console.log()}
									dateFormat="dd/mm/yy"
									locale={"pt-BR"}
									placeholder="Selecione..."
									style={{ width: "100%", height: "40px" }}
								/>
							)}
						/>
						{errors.startDate ? (
							<div className="w-100">
								{" "}
								<Error>{errors.startDate?.message}</Error>{" "}
							</div>
						) : (
							""
						)}
					</Col>

					<Col
						xs={12}
						md={6}
						className="mb-3 text-darker-grey"
					>
						<Form.Label>
							{translate("manager.subscription.end")}:
						</Form.Label>
						<br />
						<Controller
							control={control}
							name="endDate"
							render={({ field }) => (
								<Calendar
									disabled={disableFields}
									value={field.value}
									onChange={field.onChange}
									onMonthChange={() => console.log()}
									dateFormat="dd/mm/yy"
									locale={"pt-BR"}
									placeholder="Selecione..."
									style={{ width: "100%", height: "40px" }}
								/>
							)}
						/>
						{errors.endDate ? (
							<div className="w-100">
								{" "}
								<Error>{errors.endDate?.message}</Error>{" "}
							</div>
						) : (
							""
						)}
					</Col>

					<Col
						xs={6}
						md={6}
						className="mt-4 text-darker-grey d-flex justify-content-between align-items-center"
					>
						<Form.Label>
							{translate("manager.subscription.attachedDocs")}
						</Form.Label>

						<input
							ref={inputFile}
							className="d-none"
							type="file"
							multiple
							onChange={(e: any) =>
								handleAddDocs(e.currentTarget.files)
							}
						/>
						<Button
							variant="link"
							onClick={() => inputFile.current?.click()}
							style={{
								textDecoration: "none",
								color: colors.mainColors.blueberry,
							}}
							className="d-flex align-items-center gap-2"
						>
							{translate("manager.subscription.add")}{" "}
							<FaPlusCircle />
						</Button>
					</Col>

					<Col
						xs={6}
						md={6}
						className="mt-2 text-darker-grey docs"
					>
						{attachedDocs.map((doc: any, index: number) => (
							<Col
								xs={12}
								md={6}
								lg={4}
								key={index}
								className="mt-2 mb-2 doc-attached"
							>
								<CloseButton
									className="close-button-doc-attached"
									onClick={() => handleRemoveDoc(index)}
								/>{" "}
								{doc.name}
							</Col>
						))}
					</Col>

					{/* Renovação auto */}
					<Col
						xs={6}
						md={6}
						className="mt-4 mb-4  text-darker-grey"
					>
						<Form.Group className="d-flex">
							<Checkbox
								inputId="autoRenewal-check"
								name="autoRenewal-check"
								checked={autoRenewal}
								onChange={() => {
									setAutoRenewal(!autoRenewal);
									setValue("autoRenewal", !autoRenewal);
								}}
							/>

							<Form.Label
								className="ml-2"
								htmlFor="autoRenewal-check"
							>
								Renovação automática
							</Form.Label>
						</Form.Group>
					</Col>

					{/* Criar */}
					<Button onClick={handleSubmit(onSubmit)}>
						Criar assinatura
					</Button>
				</>
			)}
		</CreateSubscriptionWrapper>
	);
};
